import { CheckOutlined, WarningOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import type { ThemeConfig } from "antd";
import { navyButtonTheme } from "pages/schedule/component/navyButtonTheme";
import { CSSProperties } from "react";
import { colors } from "shared/theme/color";
import { textPx, wholeScreenPx } from "shared/constants/zoom";

/**
 * Shared chrome for the schedule popups (2026-06-09-schedule-popups-prototype):
 * a single display-font title with an optional uppercase eyebrow above it and
 * an optional alert dot beside it, plus the muted subtitle / section-label
 * styles. One source so the popups stay consistent instead of each restyling
 * its own header.
 */

// Popup button-color system (prototype): blue (navy) = primary/confirm,
// black (ink) = cancel/dismiss, red (error) = destructive. Wrap a Modal — or
// an imperative modal's footer — in <ConfigProvider theme={modalButtonsTheme}>
// so type="primary" renders navy, the default (cancel) button renders solid
// ink, and `danger` keeps the token red.
export const modalButtonsTheme: ThemeConfig = {
  // Same navy primary as the create panel's Jadwalkan (single source).
  token: navyButtonTheme.token,
  components: {
    Button: {
      defaultBg: colors.ink,
      defaultColor: colors.page,
      defaultBorderColor: colors.ink,
      // Slightly lifted ink on hover (prototype dims via opacity) — no token.
      defaultHoverBg: "#2B2B28",
      defaultHoverColor: colors.page,
      defaultHoverBorderColor: "#2B2B28",
      defaultActiveBg: colors.ink,
      defaultActiveColor: colors.page,
      defaultActiveBorderColor: colors.ink,
    },
  },
};

// Escape hatch for a plain default-styled button nested inside a
// modalButtonsTheme tree (e.g. a secondary action in a modal's BODY, not its
// footer) — modalButtonsTheme's solid-ink default-button override would
// otherwise leak onto it. Restores the app's normal outlined look, including
// hover/active (see src/shared/theme/index.ts's Button tokens + colorPrimary:
// ink, which is what those states resolve to outside any modal theme).
export const outlinedButtonTheme: ThemeConfig = {
  components: {
    Button: {
      defaultBg: colors.card,
      defaultColor: colors.ink,
      defaultBorderColor: colors.line,
      defaultHoverBg: colors.card,
      defaultHoverColor: colors.ink,
      defaultHoverBorderColor: colors.ink,
      defaultActiveBg: colors.bgSoft,
      defaultActiveColor: colors.ink,
      defaultActiveBorderColor: colors.ink,
    },
  },
};

// Uppercase micro-label above a modal title (prototype .eyebrow).
export const modalEyebrowStyle: CSSProperties = {
  display: "block",
  fontSize: textPx(11),
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: colors.ink3,
  marginBottom: 5,
};

// Muted explainer under the title (prototype .msub).
export const modalSubtitleStyle: CSSProperties = {
  display: "block",
  color: colors.ink2,
  fontSize: textPx(13),
  marginTop: 8,
  lineHeight: 1.5,
};

// Small muted label above a block in a list (prototype .clash-label).
export const modalBlockLabelStyle: CSSProperties = {
  display: "block",
  fontSize: textPx(12),
  color: colors.ink3,
  margin: "16px 0 7px",
};

export const ModalHeader = ({
  eyebrow,
  title,
  tone,
  big = false,
}: {
  eyebrow?: string;
  title: string;
  // Colored alert dot beside the title — error for clashes, amber for
  // destructive confirms, success for positive confirms.
  tone?: "error" | "amber" | "success";
  // Large numeric title (the overflow popup's slot time).
  big?: boolean;
}) => (
  <div>
    {eyebrow && <span style={modalEyebrowStyle}>{eyebrow}</span>}
    <Flex align="center" gap={9}>
      {tone && (
        <span
          // Whole screen pixels for the circle and the glyph: on a fractional
          // pixel the small SVG renders a hair up and left of centre.
          style={{
            width: wholeScreenPx(22),
            height: wholeScreenPx(22),
            borderRadius: 999,
            background:
              tone === "error"
                ? colors.error
                : tone === "success"
                ? colors.success
                : colors.amber,
            color: colors.white,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {tone === "success" ? (
            <CheckOutlined style={{ fontSize: wholeScreenPx(12) }} />
          ) : (
            <WarningOutlined style={{ fontSize: wholeScreenPx(12) }} />
          )}
        </span>
      )}
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 600,
          fontSize: textPx(big ? 26 : 20),
          letterSpacing: "-0.02em",
          lineHeight: 1.2,
          color: colors.ink,
          ...(big ? { fontFeatureSettings: '"tnum"' } : {}),
        }}
      >
        {title}
      </span>
    </Flex>
  </div>
);
